import moment from "moment";
import type { CompanyOBSetting } from "model/Company";

export type DisplayObSetting = {
  companyId: number;
  date: string | null;
  endTime: string;
  fixedValue: number | null;
  id: number[];
  percentValue: number | null;
  startTime: string;
  weekday: number | null;
};

type BuildObSettingsParams = {
  companyId: number;
  dateString: string | null;
  dayIndexes: readonly number[];
  endTime: string;
  obType: "Percent" | "Kr";
  obValue: number;
  startTime: string;
  useDate: boolean;
};

type GroupedDisplayObSettings = {
  dateList: readonly DisplayObSetting[][];
  weekList: readonly DisplayObSetting[][];
};

function hasSameCompensation(
  leftSetting: Pick<DisplayObSetting, "fixedValue" | "percentValue">,
  rightSetting: Pick<DisplayObSetting, "fixedValue" | "percentValue">,
): boolean {
  return (
    leftSetting.fixedValue === rightSetting.fixedValue &&
    leftSetting.percentValue === rightSetting.percentValue
  );
}

function sortWeekdaySettings(
  sameSettingList: readonly DisplayObSetting[],
): DisplayObSetting[] {
  return [...sameSettingList].sort((leftSetting, rightSetting) => {
    if (leftSetting.weekday === null || rightSetting.weekday === null) {
      return 0;
    }

    if (leftSetting.weekday === 0) {
      return 1;
    }

    if (rightSetting.weekday === 0) {
      return -1;
    }

    return leftSetting.weekday - rightSetting.weekday;
  });
}

export function isObSettingOvernight({
  endTime,
  startTime,
}: {
  endTime: string;
  startTime: string;
}): boolean {
  const startDate = moment("2022-12-10");
  const endDate = moment("2022-12-10");
  const parsedStartTime = moment(startTime, "HH:mm");
  const parsedEndTime = moment(endTime, "HH:mm");

  startDate.set({
    hour: parsedStartTime.get("hour"),
    minute: parsedStartTime.get("minute"),
    second: 0,
  });

  endDate.set({
    hour: parsedEndTime.get("hour"),
    minute: parsedEndTime.get("minute"),
    second: 0,
  });

  if (endTime === "00:00") {
    return false;
  }

  return endDate < startDate || startTime === endTime;
}

export function buildObSettingsToCreate({
  companyId,
  dateString,
  dayIndexes,
  endTime,
  obType,
  obValue,
  startTime,
  useDate,
}: BuildObSettingsParams): readonly CompanyOBSetting[] {
  const isOvernight = isObSettingOvernight({ endTime, startTime });
  const formattedStartTime = moment(startTime, "HH:mm").format("HH:mm:ss");
  const formattedEndTime = isOvernight
    ? "23:59:59"
    : moment(endTime, "HH:mm").subtract(1, "seconds").format("HH:mm:ss");
  const trailingEndTime = moment(endTime, "HH:mm")
    .subtract(1, "seconds")
    .format("HH:mm:ss");

  if (useDate && dateString) {
    const firstSetting: CompanyOBSetting = {
      companyId,
      date: dateString,
      endTime: formattedEndTime,
      fixedValue: obType === "Kr" ? obValue : null,
      percentValue: obType === "Percent" ? obValue : null,
      startTime: formattedStartTime,
      weekday: null,
    };

    if (!isOvernight) {
      return [firstSetting];
    }

    const secondDate = moment(dateString, "YYYY-MM-DD")
      .add(1, "days")
      .format("YYYY-MM-DD");

    return [
      firstSetting,
      {
        companyId,
        date: secondDate,
        endTime: trailingEndTime,
        fixedValue: obType === "Kr" ? obValue : null,
        percentValue: obType === "Percent" ? obValue : null,
        startTime: "00:00:01",
        weekday: null,
      },
    ];
  }

  return dayIndexes.flatMap((dayIndex) => {
    const firstSetting: CompanyOBSetting = {
      companyId,
      date: null,
      endTime: formattedEndTime,
      fixedValue: obType === "Kr" ? obValue : null,
      percentValue: obType === "Percent" ? obValue : null,
      startTime: formattedStartTime,
      weekday: dayIndex,
    };

    if (!isOvernight) {
      return [firstSetting];
    }

    const nextWeekday = dayIndex + 1 < 7 ? dayIndex + 1 : 0;

    return [
      firstSetting,
      {
        companyId,
        date: null,
        endTime: trailingEndTime,
        fixedValue: obType === "Kr" ? obValue : null,
        percentValue: obType === "Percent" ? obValue : null,
        startTime: "00:00:01",
        weekday: nextWeekday,
      },
    ];
  });
}

export function getGroupedDisplayObSettings(
  obSettings: readonly CompanyOBSetting[],
): GroupedDisplayObSettings {
  const overnightSettings: DisplayObSetting[] = [];
  const daytimeSettings: DisplayObSetting[] = [];

  obSettings.forEach((currentSetting) => {
    const foundOvernightSetting = obSettings.find((setting) => {
      const currentEndTime = moment(currentSetting.endTime, "HH:mm:ss");
      const candidateStartTime = moment(setting.startTime, "HH:mm:ss").subtract(
        2,
        "seconds",
      );
      const previousDate = moment(setting.date, "YYYY-MM-DD").subtract(
        1,
        "days",
      );
      const usesDateOvernight =
        Boolean(setting.date) &&
        previousDate.format("YYYY-MM-DD") === currentSetting.date;

      return (
        ((setting.weekday !== null &&
          currentSetting.weekday !== null &&
          setting.weekday - 1 === currentSetting.weekday) ||
          (setting.weekday !== null &&
            currentSetting.weekday !== null &&
            setting.weekday === 0 &&
            currentSetting.weekday === 6) ||
          usesDateOvernight) &&
        hasSameCompensation(setting, currentSetting) &&
        currentEndTime.format("HH:mm:ss") ===
          candidateStartTime.format("HH:mm:ss")
      );
    });

    if (
      foundOvernightSetting &&
      foundOvernightSetting.id !== undefined &&
      currentSetting.id !== undefined
    ) {
      overnightSettings.push({
        companyId: currentSetting.companyId,
        date: currentSetting.date,
        endTime: foundOvernightSetting.endTime,
        fixedValue: currentSetting.fixedValue,
        id: [foundOvernightSetting.id, currentSetting.id],
        percentValue: currentSetting.percentValue,
        startTime: currentSetting.startTime,
        weekday: currentSetting.weekday,
      });
    }
  });

  obSettings.forEach((setting) => {
    if (setting.id === undefined) {
      return;
    }

    const settingId = setting.id;
    const isMerged = overnightSettings.some((mergedSetting) =>
      mergedSetting.id.includes(settingId),
    );

    if (!isMerged) {
      daytimeSettings.push({
        ...setting,
        id: [settingId],
      });
    }
  });

  const groupedWeekSettings: DisplayObSetting[][] = [];
  const groupedDateSettings: DisplayObSetting[][] = [];

  [...daytimeSettings, ...overnightSettings].forEach((setting) => {
    if (setting.date !== null) {
      groupedDateSettings.push([setting]);
      return;
    }

    const sameGroupIndex = groupedWeekSettings.findIndex((group) => {
      const firstSetting = group[0];

      return (
        firstSetting !== undefined &&
        firstSetting.weekday !== null &&
        setting.weekday !== null &&
        firstSetting.startTime === setting.startTime &&
        firstSetting.endTime === setting.endTime &&
        hasSameCompensation(firstSetting, setting)
      );
    });

    if (sameGroupIndex === -1) {
      groupedWeekSettings.push([setting]);
      return;
    }

    groupedWeekSettings[sameGroupIndex] = [
      ...groupedWeekSettings[sameGroupIndex],
      setting,
    ];
  });

  return {
    dateList: groupedDateSettings,
    weekList: groupedWeekSettings
      .map(sortWeekdaySettings)
      .filter((group) => group.length > 0),
  };
}
